import {
  Cabin,
  CabinCategoryID,
  IbePriceModelCode,
  MainCabinCategoryID,
  PremiumCabinCategoryID,
} from '@gecko-akh/pkg-cruise-api-types/src/iris'
import { Cabin as BewotecCabin, FindCabinsRequest } from '../../../bewotec/openapi'
import { handleError } from '../../../general/errorHandling'
import { appHelper, mapAgencyForRequest } from '../../../store/app/helper'
import { IPaxConfig } from '../../../store/catalog/interfaces'

export const getCabins = async (
  paxConfig: IPaxConfig,
  cruiseID: string,
  categoryId: PremiumCabinCategoryID,
  agencyId: string,
  priceModel: IbePriceModelCode
): Promise<Cabin[]> => {
  try {
    const cabinsRequest: FindCabinsRequest = {
      cruiseId: cruiseID,
      cabinCategoryCode: categoryId,
      agencyId: mapAgencyForRequest(agencyId),
      priceModelCode: priceModel,
      adults: paxConfig.adults,
      juveniles: paxConfig.juveniles,
      children: paxConfig.children,
      babies: paxConfig.babies,
      displayAccessibleCabins: true,
    }
    const bewotecCabins = await appHelper.bewotecMicroServiceApi().findCabins(cabinsRequest)

    return mapCabins(bewotecCabins)
  } catch (error) {
    handleError({ error, origin: 'getCabins' })
    return []
  }
}

// TODO: remove and use bewotec cabins directly
const mapCabins = (cabins: BewotecCabin[]): Cabin[] => {
  const mappedCabins: Cabin[] = cabins.map(cabin => {
    return mapCabin(cabin)
  })

  return mappedCabins
}

export const mapCabin = (cabin: BewotecCabin): Cabin => {
  // TODO: remove all fallbacks
  return {
    id: cabin.cabinNumber,
    cabinCategory: (cabin.cabinCategoryCode as CabinCategoryID) ?? '',
    mainCabinCategory: (cabin.mainCategoryCode as MainCabinCategoryID) ?? '',
    // remove; not used
    minPerson: 0,
    maxPerson: cabin.maxOccupancy ?? 0,
    area: cabin.size ?? 0,
    balcony: cabin.balcony ?? 0,
    balconyType: cabin.balconyType ?? '',
    layout: cabin.layoutImageUrl ?? '',
    view: cabin.viewImageUrl ?? '',
    bed: cabin.beds ?? 0,
    babyBed: !!cabin.babyBed,
    sofaBed: cabin.sofaBeds ?? 0,
    sepBeds: cabin.sepBeds ?? 0,
    doubleBed: cabin.doubleBeds ?? 0,
    wallPullman: cabin.wallPullmans ?? 0,
    cailingPullman: cabin.ceilingPullmans ?? 0,
    window: 0,
    location: mapLocation(cabin.side),
    // TODO: add windows if portholes === true
    porthole: cabin.portholes ? 1 : 0,
    // TODO: use own type and make it a string
    connection: cabin.connectedCabinNumber ? parseInt(cabin.connectedCabinNumber, 10) : 0,
    connectionDoor: !!cabin.connectedCabinNumber,
    position: mapPosition(cabin.location),
    directionOfTravel: !!cabin.directionOfTravel,
    closeToStairs: !!cabin.closeToStairs,
    closeToWellness: !!cabin.closeToWellness,
    closeToRestaurant: !!cabin.closeToRestaurant,
    sepSundeck: !!cabin.sepSundeck,
    visibility: !!cabin.limitedVisibility,
    isAccessible: !!cabin.handicappedAccessible,
    // TODO: remove; not used
    information: '',
    twoBathrooms: !!cabin.twoBathrooms,
    walkinWardrobe: !!cabin.walkInWardrobe,
    deck: cabin.deck ?? 0,
    configCode: '',
  }
}

const mapLocation = (side: string | undefined) => {
  switch (side) {
    case 'S':
      return 'steuerbord'
    case 'P':
      return 'backbord'
    // TODO: improve default
    default:
      return 'backbord'
  }
}

const mapPosition = (location: string | undefined) => {
  switch (location) {
    case 'M':
      return 'mittig'
    case 'F':
      return 'vorne'
    case 'A':
      return 'hinten'
    default:
      // TODO: improve default
      return 'mittig'
  }
}

export const filterDuplicates = (storedCabins: Cabin[], cabins: Cabin[]) => {
  for (const cabin of cabins) {
    if (!storedCabins.find(storedCabin => storedCabin.id === cabin.id)) {
      storedCabins.push(cabin)
    }
  }

  return storedCabins
}

export const cabinHelper = {
  mapCabin,
  getCabins,
  filterDuplicates,
}

// ------- exports for testing purposes -------

export const exportForTesting = {
  mapCabins,
  mapLocation,
  mapPosition,
}
